import React from 'react';
import { Image, Camera, Users, Square, Heart, Target, Edit3 } from 'lucide-react';

interface Step8Data {
  image_style: string;
}

interface Step8ImageStyleProps {
  data: Step8Data;
  onChange: (data: Step8Data) => void;
}

const IMAGE_STYLE_OPTIONS = [
  {
    value: 'professioneel-zakelijk',
    label: 'Professioneel & zakelijk',
    description: 'Nette kantooromgevingen, mensen in zakelijke context, clean en modern',
    icon: Camera
  },
  {
    value: 'authentiek-persoonlijk',
    label: 'Authentiek & persoonlijk',
    description: 'Echte mensen in natuurlijke omgevingen, warm en toegankelijk',
    icon: Users,
    recommended: true
  },
  {
    value: 'modern-minimalistisch',
    label: 'Modern & minimalistisch',
    description: 'Strakke designs, veel wit, geometrische vormen, tech-feel',
    icon: Square
  },
  {
    value: 'warm-menselijk',
    label: 'Warm & menselijk',
    description: 'Close-ups van mensen, emotie, verbinding, vriendelijk',
    icon: Heart
  },
  {
    value: 'product-focused',
    label: 'Product-focused',
    description: 'Focus op producten, details, e-commerce stijl',
    icon: Target
  },
];

export function Step8ImageStyle({ data, onChange }: Step8ImageStyleProps) {
  const [showCustomField, setShowCustomField] = React.useState(false);
  const [customStyle, setCustomStyle] = React.useState('');

  const handleSelectStyle = (style: string) => {
    onChange({ ...data, image_style: style });
    setShowCustomField(false);
    setCustomStyle('');
  };

  const handleCustomStyle = (value: string) => {
    setCustomStyle(value);
    onChange({ ...data, image_style: value });
  };

  const toggleCustomField = () => {
    setShowCustomField(!showCustomField);
    if (!showCustomField) {
      onChange({ ...data, image_style: customStyle });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
          <Image className="w-4 h-4" />
          Kies je visuele stijl
        </label>
        <div className="space-y-3">
          {IMAGE_STYLE_OPTIONS.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.value}
                onClick={() => handleSelectStyle(option.value)}
                className={`w-full p-4 border-2 rounded-xl text-left transition-all ${
                  data.image_style === option.value
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <Icon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 mb-1 flex items-center gap-2">
                      {option.label}
                      {option.recommended && (
                        <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">
                          Aanbevolen
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">{option.description}</div>
                  </div>
                </div>
              </button>
            );
          })}

          <button
            onClick={toggleCustomField}
            className={`w-full p-4 border-2 rounded-xl text-left transition-all ${
              showCustomField || (customStyle && !IMAGE_STYLE_OPTIONS.some(opt => opt.value === data.image_style))
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start gap-3">
              <Edit3 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-gray-900 mb-1">Anders</div>
                {showCustomField && (
                  <input
                    type="text"
                    value={customStyle}
                    onChange={(e) => handleCustomStyle(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Beschrijf je eigen stijlvoorkeur..."
                    autoFocus
                  />
                )}
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
